/**
 * Collection landing page — one indexable URL per category keyword.
 *
 * This is the page shape that ranks for commercial queries in this niche. Verified against
 * the results for "artificial flowers for home decoration": neither of the top two results
 * was a homepage or a product page, both were category pages carrying the keyword in the
 * URL, the <title> and the <h1>. Before this route the only category view here was
 * /shop?category=<name>, which the prerender filter excludes as a query URL, so there was
 * nothing for those queries to match.
 *
 * Definitions live in src/config/collections.ts, shared with the prerender list and the
 * sitemap generator so the three cannot drift apart.
 */

import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ProductGrid } from "@/features/products/components/product-grid";
import { Reveal } from "@/components/common/reveal";
import { BreadcrumbNav } from "@/components/common/breadcrumb-nav";
import { EmptyState } from "@/components/common/empty-state";
import { useLiveProducts } from "@/features/products/hooks/use-live-products";
import { getCollection, collections } from "@/config/collections";
import { getCollectionPageSchema, getFaqSchema, safeJsonLd } from "@/lib/seo";
import { getProductSlug } from "@/lib/products";
import { siteConfig } from "@/config/site";

export const Route = createFileRoute("/collections/$slug")({
  // An unknown slug must be a real 404, not a soft one. The Worker's SPA fallback answers
  // any unmatched path with the prerendered homepage and a 200, which Google reads as
  // duplicate content -- the same defect the four dead footer links already have. Throwing
  // notFound() here keeps invented URLs like /collections/artificial-cactus out of that trap.
  loader: ({ params }) => {
    const collection = getCollection(params.slug);
    if (!collection) throw notFound();
    return { collection };
  },
  head: ({ params }) => {
    const collection = getCollection(params.slug);
    if (!collection) return {};
    const canonicalUrl = `${siteConfig.url}/collections/${collection.slug}`;
    return {
      meta: [
        { title: `${collection.title} — ${siteConfig.name}` },
        { name: "description", content: collection.description },
        { property: "og:title", content: `${collection.title} — ${siteConfig.shortName}` },
        { property: "og:description", content: collection.description },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: collection.title },
        { name: "twitter:description", content: collection.description },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  component: CollectionDetail,
});

function CollectionDetail() {
  const { slug } = Route.useParams();
  const { products } = useLiveProducts();
  const collection = getCollection(slug);

  // The loader throws for an unknown slug, so this is unreachable in practice. Kept as a
  // type guard rather than a non-null assertion.
  if (!collection) return null;

  const items = products.filter(
    (p) => (p.category || "").toLowerCase().trim() === collection.category.toLowerCase().trim(),
  );

  const canonicalUrl = `${siteConfig.url}/collections/${collection.slug}`;

  const collectionSchema = getCollectionPageSchema({
    name: collection.title,
    description: collection.description,
    url: canonicalUrl,
    productUrls: items.map((p) => `${siteConfig.url}/product/${getProductSlug(p)}`),
  });

  // Rendered visibly below as well. Google requires FAQ markup to correspond to content the
  // visitor can actually read on the page; markup alone would be a policy violation of the
  // same kind as the review stars removed from getProductSchema.
  const faqSchema = getFaqSchema(collection.faqs);

  const siblings = collections.filter((c) => c.slug !== collection.slug);

  return (
    <div className="section-container pb-28 pt-24 md:pt-28">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(collectionSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(faqSchema) }}
      />

      <BreadcrumbNav
        items={[
          { label: "Home", to: "/" },
          { label: "Collections", to: "/collections" },
          { label: collection.heading },
        ]}
      />

      <Reveal>
        <header className="mt-6 max-w-3xl">
          <h1 className="font-display text-4xl md:text-6xl font-bold text-foreground">
            {collection.heading}
          </h1>
          {/* Where the brand voice lives: the h1 above carries the search term, this line
              carries the tone. */}
          <p className="mt-5 font-display text-xl md:text-2xl text-emerald-800">
            {collection.subheading}
          </p>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground">
            {collection.intro}
          </p>
        </header>
      </Reveal>

      <div className="mt-14">
        {items.length === 0 ? (
          <EmptyState
            title={`No ${collection.heading.toLowerCase()} in stock`}
            description="This collection is being restocked. Browse the full range in the meantime."
            action={{ label: "Shop all", to: "/shop" }}
          />
        ) : (
          // `loading` is deliberately NOT forwarded to ProductGrid, matching shop.tsx.
          // useLiveProducts() reports loading:true on its first render, so passing it would
          // make the prerender emit ProductGridSkeleton and bake a page with zero product
          // links and zero product names into the static HTML — the crawler would see an
          // empty category page while the JSON-LD ItemList claimed N products. The catalogue
          // is available synchronously (getServerSnapshot returns seedProducts), so there is
          // nothing to wait for at build time.
          <ProductGrid products={items} columns={3} priorityCount={3} />
        )}
      </div>

      <section className="mt-24 max-w-3xl">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground">
          Questions about {collection.heading.toLowerCase()}
        </h2>
        <dl className="mt-8 space-y-8">
          {collection.faqs.map((faq) => (
            <div key={faq.question}>
              <dt className="font-semibold text-foreground">{faq.question}</dt>
              <dd className="mt-2 leading-relaxed text-muted-foreground">{faq.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Internal links between the four collections. A category page with no inbound links
          from anywhere but /collections is close to orphaned; cross-linking gives a crawler
          a path between all four and passes what little authority the site has around. */}
      <section className="mt-24 border-t border-stone-200 pt-12">
        <h2 className="font-display text-xl md:text-2xl font-bold text-foreground">
          Other collections
        </h2>
        <ul className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
          {siblings.map((c) => (
            <li key={c.slug}>
              <Link
                to="/collections/$slug"
                params={{ slug: c.slug }}
                className="rule-link text-base font-semibold text-emerald-800 hover:text-emerald-900"
              >
                {c.heading}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
